import type { CanvasOptions } from './types/canvas-options';
import type { CanvasEvents, CanvasPointerEventDetail } from './types/canvas-events';
import type { SelectableCanvasOptions } from './types/selectable-canvas-options';
import { getRuntimeWindow, invertMatrix, Matrix2D, Point, transformPoint } from '@imagise/shared';
import { InteractiveObject } from './editor-object';
import { SelectableCanvas } from './selectable-canvas';

const getDomWindow = (): (Window & typeof globalThis) | undefined => {
  try {
    return getRuntimeWindow();
  } catch {
    return undefined;
  }
};

const getEventConstructor = (): typeof Event | undefined => {
  const runtimeWindow = getDomWindow() as (Window & { Event?: typeof Event }) | undefined;
  return runtimeWindow?.Event ?? (typeof Event !== 'undefined' ? Event : undefined);
};

const getTouchEventConstructor = (): typeof TouchEvent | undefined => {
  const runtimeWindow = getDomWindow() as (Window & { TouchEvent?: typeof TouchEvent }) | undefined;
  return runtimeWindow?.TouchEvent ?? (typeof TouchEvent !== 'undefined' ? TouchEvent : undefined);
};

const PASSIVE_FALSE: AddEventListenerOptions = { passive: false };

// In the legacy (non-PointerEvent) path both touch and mouse listeners are
// attached; browsers re-dispatch a tap as synthetic mouse events within this
// window, which would double-fire the pointer pipeline.
const SYNTHETIC_MOUSE_SUPPRESSION_MS = 500;

type PointerLikeEvent =
  | PointerEvent
  | MouseEvent
  | TouchEvent
  | (Partial<PointerEvent> & { clientX: number; clientY: number });

type NormalizedPointer = {
  scenePoint: Point;
  viewportPoint: Point;
  button?: number;
  isPrimary?: boolean;
  originalEvent?: Event;
};

type PointerState = {
  isDown: boolean;
  target?: InteractiveObject;
  selecting: boolean;
  dragging: boolean;
  moved: boolean;
  start: Point;
  last: Point;
};

export class Canvas<EventSpec extends CanvasEvents = CanvasEvents>
  extends SelectableCanvas<EventSpec>
  implements CanvasOptions
{
  enablePointerEvents = true;
  allowTouchScrolling = false;
  stopContextMenu = true;

  private pointerState?: PointerState;
  private lastTouchInputAt = 0;
  private listenersAttached = false;
  private detachListeners?: Array<() => void>;
  private readonly boundPointerDown = (event: Event) => this.handlePointerDown(event as PointerLikeEvent);
  private readonly boundPointerMove = (event: Event) => this.handlePointerMove(event as PointerLikeEvent);
  private readonly boundPointerUp = (event: Event) => this.handlePointerUp(event as PointerLikeEvent);
  private readonly boundContextMenu = (event: Event) => {
    event.preventDefault();
  };

  constructor(element?: string | HTMLCanvasElement, options: CanvasOptions = {}) {
    super(element, options);
    this.applyCanvasOptions(options);
    this.attachInteractionEvents();
  }

  override attachToElement(element: string | HTMLCanvasElement): void {
    // Re-wire interaction listeners around the element swap; otherwise the
    // stale listeners keep the old element alive and the new one stays inert.
    this.detachInteractionEvents();
    super.attachToElement(element);
    this.attachInteractionEvents();
  }

  override dispose(): void {
    this.detachInteractionEvents();
    super.dispose();
  }

  /**
   * Programmatically handles pointer down logic for normalized inputs.
   */
  handlePointerDown(event: PointerLikeEvent): void {
    this.onPointerDown(event);
  }

  /**
   * Programmatically handles pointer move logic for normalized inputs.
   */
  handlePointerMove(event: PointerLikeEvent): void {
    this.onPointerMove(event);
  }

  /**
   * Programmatically handles pointer up logic for normalized inputs.
   */
  handlePointerUp(event: PointerLikeEvent): void {
    this.onPointerUp(event);
  }

  protected applyCanvasOptions(options: CanvasOptions = {}): void {
    if (typeof options.enablePointerEvents === 'boolean') {
      this.enablePointerEvents = options.enablePointerEvents;
    }
    if (typeof options.allowTouchScrolling === 'boolean') {
      this.allowTouchScrolling = options.allowTouchScrolling;
    }
    if (typeof options.stopContextMenu === 'boolean') {
      this.stopContextMenu = options.stopContextMenu;
    }
    this.applySelectableOptions(options as SelectableCanvasOptions);
  }

  private attachInteractionEvents(): void {
    if (this.listenersAttached) {
      return;
    }
    const canvas = this.upperCanvasEl ?? this.getElement();
    const win = getDomWindow();
    if (!canvas || !win) {
      return;
    }
    if (this.shouldUsePointerEvents()) {
      this.addDomListener(canvas, 'pointerdown', this.boundPointerDown, PASSIVE_FALSE);
      this.addDomListener(win, 'pointermove', this.boundPointerMove, PASSIVE_FALSE);
      this.addDomListener(win, 'pointerup', this.boundPointerUp, PASSIVE_FALSE);
      this.addDomListener(win, 'pointercancel', this.boundPointerUp, PASSIVE_FALSE);
    } else {
      this.addDomListener(canvas, 'mousedown', this.boundPointerDown, PASSIVE_FALSE);
      this.addDomListener(win, 'mousemove', this.boundPointerMove, PASSIVE_FALSE);
      this.addDomListener(win, 'mouseup', this.boundPointerUp, PASSIVE_FALSE);
      this.addDomListener(canvas, 'touchstart', this.boundPointerDown, PASSIVE_FALSE);
      this.addDomListener(win, 'touchmove', this.boundPointerMove, PASSIVE_FALSE);
      this.addDomListener(win, 'touchend', this.boundPointerUp, PASSIVE_FALSE);
      this.addDomListener(win, 'touchcancel', this.boundPointerUp, PASSIVE_FALSE);
    }
    if (this.stopContextMenu) {
      this.addDomListener(canvas, 'contextmenu', this.boundContextMenu);
    }
    this.listenersAttached = true;
  }

  private detachInteractionEvents(): void {
    if (!this.listenersAttached) {
      return;
    }
    this.detachListeners?.forEach(unsubscribe => unsubscribe());
    this.detachListeners = [];
    this.listenersAttached = false;
  }

  private addDomListener(
    target: EventTarget | undefined,
    type: string,
    listener: EventListenerOrEventListenerObject,
    options?: boolean | AddEventListenerOptions
  ): void {
    if (!target) {
      return;
    }
    target.addEventListener(type, listener, options);
    if (!this.detachListeners) {
      this.detachListeners = [];
    }
    this.detachListeners.push(() => target.removeEventListener(type, listener, options));
  }

  private shouldUsePointerEvents(): boolean {
    const runtimeWindow = getDomWindow() as (Window & { PointerEvent?: typeof PointerEvent }) | undefined;
    return this.enablePointerEvents && Boolean(runtimeWindow?.PointerEvent);
  }

  private onPointerDown(event: PointerLikeEvent): void {
    if (this.isSyntheticMouseAfterTouch(event)) {
      return;
    }
    if (!this.allowTouchScrolling) {
      event.preventDefault?.();
    }
    const pointer = this.normalizePointerEvent(event);
    // Only the main button interacts with objects/selection; right and middle
    // clicks must not start a group selection.
    const isMainButton = pointer.button === 0 || pointer.button === undefined;
    const target = isMainButton ? this.findTarget(pointer.scenePoint) : undefined;
    const selecting = isMainButton && !target && this.selection;

    this.pointerState = {
      isDown: true,
      target: target ?? undefined,
      selecting,
      dragging: Boolean(target && this.isObjectMovable(target)),
      moved: false,
      start: pointer.scenePoint,
      last: pointer.scenePoint,
    };

    if (target) {
      this.setActiveObject(target, 'pointer');
    } else if (selecting) {
      this.beginGroupSelection(pointer.scenePoint);
    }

    this.updateCursor(target);
    this.firePointerEvent('pointer:down', pointer, target);
  }

  private onPointerMove(event: PointerLikeEvent): void {
    const isDragging = this.pointerState?.isDown === true;
    // preventDefault page-wide is only justified while a drag is in progress;
    // the move listener lives on window to keep drags alive outside the canvas.
    if (isDragging && !this.allowTouchScrolling) {
      event.preventDefault?.();
    }
    if (!isDragging && !this.isEventTargetWithinCanvas(event)) {
      return;
    }
    const pointer = this.normalizePointerEvent(event);
    const hovered = this.findTarget(pointer.scenePoint);
    this.updateCursor(hovered);
    if (this.pointerState?.selecting) {
      this.updateGroupSelection(pointer.scenePoint);
    }
    if (this.pointerState?.dragging) {
      this.translateDraggedObject(pointer);
    }
    if (this.pointerState) {
      this.pointerState.last = pointer.scenePoint;
    }
    this.firePointerEvent('pointer:move', pointer, hovered);
  }

  private onPointerUp(event: PointerLikeEvent): void {
    if (this.isSyntheticMouseAfterTouch(event)) {
      return;
    }
    const wasDown = this.pointerState?.isDown === true;
    if (wasDown && !this.allowTouchScrolling) {
      event.preventDefault?.();
    }
    if (!wasDown && !this.isEventTargetWithinCanvas(event)) {
      return;
    }
    const pointer = this.normalizePointerEvent(event);
    const hovered = this.findTarget(pointer.scenePoint);
    if (this.pointerState?.selecting) {
      this.finalizeGroupSelection({ reason: 'pointer' });
    }
    this.finalizeDraggedObject(pointer);
    this.pointerState = undefined;
    this.updateCursor(hovered);
    this.firePointerEvent('pointer:up', pointer, hovered);
  }

  private isObjectMovable(object: InteractiveObject): boolean {
    return (
      object.selectable !== false &&
      object.evented !== false &&
      !(object.lockMovementX && object.lockMovementY)
    );
  }

  private translateDraggedObject(pointer: NormalizedPointer): void {
    const state = this.pointerState;
    const target = state?.target;
    if (!state || !target) {
      return;
    }

    const deltaX = target.lockMovementX ? 0 : pointer.scenePoint.x - state.last.x;
    const deltaY = target.lockMovementY ? 0 : pointer.scenePoint.y - state.last.y;
    if (!deltaX && !deltaY) {
      return;
    }

    target.left += deltaX;
    target.top += deltaY;
    target.isMoving = true;
    target.setCoords();
    state.moved = true;
    this.fire('object:moving' as keyof EventSpec, {
      target,
      pointer: {
        scene: pointer.scenePoint,
        viewport: pointer.viewportPoint,
      },
      originalEvent: pointer.originalEvent,
    } as EventSpec[keyof EventSpec]);
    // Dragging must repaint synchronously: a queued animation frame may lag
    // behind the pointer (or never fire in throttled/background contexts),
    // leaving a stale copy of the object at its previous position.
    this.cancelRenderAll();
    this.renderAll();
  }

  private finalizeDraggedObject(pointer: NormalizedPointer): void {
    const state = this.pointerState;
    const target = state?.target;
    if (!state || !target || !state.moved) {
      return;
    }

    target.isMoving = false;
    target.setCoords();
    this.fire('object:modified' as keyof EventSpec, {
      target,
      pointer: {
        scene: pointer.scenePoint,
        viewport: pointer.viewportPoint,
      },
      originalEvent: pointer.originalEvent,
    } as EventSpec[keyof EventSpec]);
    this.cancelRenderAll();
    this.renderAll();
  }

  private isSyntheticMouseAfterTouch(event: PointerLikeEvent): boolean {
    const touchEventConstructor = getTouchEventConstructor();
    if (touchEventConstructor && event instanceof touchEventConstructor) {
      this.lastTouchInputAt = Date.now();
      return false;
    }
    const type = (event as Event).type ?? '';
    return (
      type.startsWith('mouse') &&
      Date.now() - this.lastTouchInputAt < SYNTHETIC_MOUSE_SUPPRESSION_MS
    );
  }

  private isEventTargetWithinCanvas(event: PointerLikeEvent): boolean {
    const canvas = this.upperCanvasEl ?? this.getElement();
    const target = (event as Event).target;
    return (
      !!canvas &&
      target instanceof Node &&
      (target === canvas || canvas.contains(target))
    );
  }

  private updateCursor(target?: InteractiveObject): void {
    const cursor = target?.hoverCursor || (target ? this.hoverCursor : this.defaultCursor);
    this.setCursor(cursor);
  }

  private firePointerEvent(
    type: 'pointer:down' | 'pointer:move' | 'pointer:up',
    pointer: NormalizedPointer,
    target?: InteractiveObject
  ): void {
    const detail: CanvasPointerEventDetail = {
      pointer: {
        scene: pointer.scenePoint,
        viewport: pointer.viewportPoint,
      },
      originalEvent: pointer.originalEvent,
      target,
      button: pointer.button,
      isPrimary: pointer.isPrimary,
    };
    this.fire(type as keyof EventSpec, detail as EventSpec[keyof EventSpec]);
  }

  private normalizePointerEvent(event: PointerLikeEvent): NormalizedPointer {
    const { clientX, clientY, button, isPrimary } = this.extractClientPoint(event);
    const canvas = this.upperCanvasEl ?? this.getElement();
    const rect = canvas?.getBoundingClientRect
      ? canvas.getBoundingClientRect()
      : {
          left: 0,
          top: 0,
          width: this.width || 1,
          height: this.height || 1,
        };
    const relX = clientX - rect.left;
    const relY = clientY - rect.top;
    const scaleX = rect.width ? this.width / rect.width : 1;
    const scaleY = rect.height ? this.height / rect.height : 1;
    const viewportPoint = new Point(relX * scaleX, relY * scaleY);
    const inverse = invertMatrix(this.viewportTransform as Matrix2D);
    const scenePoint = transformPoint(viewportPoint, inverse);
    const eventConstructor = getEventConstructor();
    const originalEvent = eventConstructor && event instanceof eventConstructor ? event : undefined;
    return {
      scenePoint,
      viewportPoint,
      button,
      isPrimary,
      originalEvent,
    };
  }

  private extractClientPoint(input: PointerLikeEvent): {
    clientX: number;
    clientY: number;
    button?: number;
    isPrimary?: boolean;
  } {
    const touchEventConstructor = getTouchEventConstructor();
    const isTouch = touchEventConstructor && input instanceof touchEventConstructor;
    if (isTouch) {
      const touch = input.changedTouches?.[0] || input.touches?.[0];
      if (touch) {
        return {
          clientX: touch.clientX,
          clientY: touch.clientY,
          button: 0,
          isPrimary: true,
        };
      }
    }
    return {
      clientX: (input as PointerEvent).clientX ?? 0,
      clientY: (input as PointerEvent).clientY ?? 0,
      button: (input as PointerEvent).button,
      isPrimary: (input as PointerEvent).isPrimary,
    };
  }
}
